
















































































import { computed, defineComponent, getCurrentInstance, ref } from "@vue/composition-api";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import { checkAuthorities } from "@scrile/tools/dist/lib/SecurityHelpers";
import useAuthorisedOnly from "@/hooks/useAuthorisedOnly";
import useAuthUser from "@/hooks/useAuthUser";
import providers from "@/lib/api";
import { $t } from "@/lib/i18nHelpers";
import { timeAgo } from "@/lib/time";
import config from "@/config";
import { AuthUserAuthorities, LoginSignUpModalData } from "@/types";
import BaseButton from "@/components/BaseButton";
import BaseConfirm from "@/components/BaseConfirm";
import BaseIcon from "@/components/BaseIcon";
import BaseMenu from "@/components/BaseMenu";
import Share from "@/components/Share";
import UserFollowSubscribeModal from "@/components/UserFollowSubscribeModal";
import ProfileHeader from "../ProfileHeader";
import ProfileStatistic from "../ProfileStatistic";
import QuestionaryInfoPrinter from "../QuestionaryInfoPrinter";

export default defineComponent({
  name: "ProfileInfoView",
  components: {
    QuestionaryInfoPrinter,
    ProfileHeader,
    BaseButton,
    BaseMenu,
    BaseIcon,
    BaseConfirm,
    ProfileStatistic,
    Share,
    UserFollowSubscribeModal,
  },
  props: {
    user: { type: Object as () => UserPublicData, required: true },
    questionaryInfo: { type: Object as () => Record<string, any>, default: () => ({}) },
    isFollowing: { type: Boolean, required: true },
    isBlocked: { type: Boolean, required: false },
    isAuthor: { type: Boolean, required: true },
    hasSubscriptionLevels: { type: Boolean, required: true },
    hasContentCount: { type: Boolean, required: true },
    verifiedIconSize: { type: [Number, String], required: false, default: 16 },
  },
  setup(props, { emit }) {
    const showFollow = ref(false);
    const showUnfollowConfirm = ref(false);

    const screenName = computed<string>(() => props.user.screenName);
    const username = computed<string>(() => props.user.username);
    const verified = computed<boolean>(() => props.user.verified);

    const showOnlineStatus = computed<boolean>(() => config.featureUserOnlineStatus);

    const { authorities } = useAuthUser();
    const canEditInfo = computed<boolean>(() => {
      const requiredAuthorities: AuthUserAuthorities = props.isAuthor
        ? ["updateMyQuestionaryInfo", "updateMyPersonalInfo", "updateMyFile"]
        : ["updateAnyQuestionaryInfo", "updateAnyPersonalInfo", "updateAnyFile"];
      return checkAuthorities(authorities.value, requiredAuthorities);
    });

    const lastOnline = computed(() => {
      if (props.user.onlineStatus?.isOnline) return $t("Online");
      if (!props.user.onlineStatus?.lastOnlineTime) return "";
      return $t(`Last visited {0}`, [timeAgo(props.user.onlineStatus.lastOnlineTime, { addSuffix: true })]);
    });

    const isDisabled = computed<boolean>(() => props.user.disabled ?? true);
    const showSubscribeButton = computed<boolean>(() => {
      return (
        !isDisabled.value &&
        !props.isBlocked &&
        !props.isAuthor &&
        ((props.hasSubscriptionLevels && props.hasContentCount) || !props.isFollowing)
      );
    });

    const showPanelInfoUnfollow = computed<boolean>(
      () => !isDisabled.value && !props.isBlocked && !props.isAuthor && !props.isFollowing
    );

    const onSignUpSuccess = async () => {
      await providers.FollowingsProvider.toggle({ id: props.user.id });
      emit("onFollowUpdate", true);
    };
    const loginSignUpModalData: LoginSignUpModalData = { onSignUpSuccess };
    const onUnfollowClick = () => {
      showFollow.value = true;
      showUnfollowConfirm.value = false;
    };
    const onFollowingClick = useAuthorisedOnly(() => (showFollow.value = true), loginSignUpModalData);

    const vm = getCurrentInstance();
    const sendMessage = useAuthorisedOnly(async () => {
      await vm?.$router.push({ name: "messages", params: { userId: props.user.id } });
    }, loginSignUpModalData);

    return {
      username,
      verified,
      lastOnline,
      showFollow,
      isDisabled,
      screenName,
      canEditInfo,
      showOnlineStatus,
      showUnfollowConfirm,
      showSubscribeButton,
      showPanelInfoUnfollow,
      sendMessage,
      onFollowingClick,
      onUnfollowClick,
    };
  },
});
